import React, { useState, useEffect, useCallback } from 'react';
import PageLayout from '../components/PageLayout';
import { listInstruments, getAnalysisJobStatus, listAnalysisJobs, deleteAnalysisJob } from '../api/api';
import { InstrumentDataStatusDisplay, AnalysisJobStatusDisplay } from '../utils/analysisConstants';
import AnalysisJobForm from '../components/analysis/AnalysisJobForm';
import AddInstrumentForm from '../components/analysis/AddInstrumentForm'; // Import AddInstrumentForm
import { Link } from 'react-router-dom';

const POLLING_INTERVAL = 5000; // 5 seconds
// Interval for refreshing instruments list after adding one, to see PENDING_INITIAL_DOWNLOAD status update
const INSTRUMENT_REFRESH_INTERVAL = 10000; // 10 seconds 
// No longer using LOCAL_STORAGE_SUBMITTED_JOBS_KEY

const normalizeListResponse = (data) => {
  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data?.results)) {
    return data.results;
  }

  return [];
};

const HistoricalAnalysisPage = () => {
  const [instruments, setInstruments] = useState([]);
  const [isLoadingInstruments, setIsLoadingInstruments] = useState(true);
  const [errorInstruments, setErrorInstruments] = useState(null);
  const [submittedJobs, setSubmittedJobs] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(true); // Now for loading jobs from API
  const [pageError, setPageError] = useState(null);
  const [instrumentMessage, setInstrumentMessage] = useState(null); // For messages related to adding instruments

  const fetchInstruments = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoadingInstruments(true);
      const data = await listInstruments();
      setInstruments(normalizeListResponse(data));
      setErrorInstruments(null);
    } catch (error) {
      console.error("Failed to fetch instruments:", error);
      setErrorInstruments(error.toString() || "Failed to load instruments.");
      setInstruments([]);
    } finally {
      setIsLoadingInstruments(false);
    }
  }, []); // Added closing parenthesis and bracket for useCallback

  const fetchSubmittedJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      const jobsData = await listAnalysisJobs();
      // API already orders by creation date, most recent first.
      setSubmittedJobs(normalizeListResponse(jobsData));
      setPageError(null); // Clear previous errors
    } catch (error) {
      console.error("Failed to fetch submitted jobs:", error);
      setPageError(error.detail || error.message || "Failed to load submitted jobs.");
      setSubmittedJobs([]); // Clear jobs on error
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    fetchInstruments();
    fetchSubmittedJobs();
  }, [fetchInstruments, fetchSubmittedJobs]);

  const handleInstrumentAdded = (newlyAddedInstrument) => {
    // newInstrument might contain the full object or just a success indicator
    // We should refresh the list to get the latest statuses from the backend
    setInstrumentMessage(`Instrument ${newlyAddedInstrument.symbol || 'new instrument'} submitted. Refreshing list...`);
    // Optionally, add to list immediately with PENDING status if API doesn't return it fast enough
    // Or just rely on fetchInstruments to update.
    setTimeout(() => {
      fetchInstruments(false); // Fetch without full page loading indicator
      setInstrumentMessage(null); // Clear message after a bit
    }, 2000); // Give a moment for backend to process before refetch

    // More robust: poll fetchInstruments a few times if status is PENDING_INITIAL_DOWNLOAD
    let refreshCount = 0;
    const refreshInterval = setInterval(() => {
      if (refreshCount < 5) { // Poll 5 times (e.g. over 50 seconds)
        fetchInstruments(false);
        refreshCount++;
      } else {
        clearInterval(refreshInterval);
      }
    }, INSTRUMENT_REFRESH_INTERVAL);
  };

  const handleInstrumentAddError = useCallback((errorMessage) => {
    setPageError(`Failed to add instrument: ${errorMessage}`);
    setInstrumentMessage(null);
  }, []);

  const handleJobSubmitted = async (newJobInfo) => {
    // newJobInfo is the response from POST /api/analysis/analyze/
    // After successful submission, refresh the list of jobs from the server
    // to get the most up-to-date list including the new job.
    setPageError(null); // Clear previous form errors
    // Optionally, provide immediate feedback or add to list optimistically
    // For simplicity and consistency, we'll just refetch.
    try {
      // The submitAnalysisJob is already called by AnalysisJobForm.
      // Here, we just need to react to its success.
      // The onJobSubmitted callback in AnalysisJobForm now receives (response, originalFormData)
      // So newJobInfo here is the API response.
      setInstrumentMessage(`Job ${newJobInfo.job_id} submitted. Refreshing job list...`);
      await fetchSubmittedJobs(); // Refetch all jobs
      setInstrumentMessage(null);
    } catch (error) {
      // This catch is more for if fetchSubmittedJobs fails after a successful submission
      console.error("Error refreshing job list after submission:", error);
      setPageError("Job submitted, but failed to refresh job list.");
    }
  };

  const handleFormError = useCallback((errorMessage) => { // This is for AnalysisJobForm errors
    setPageError(errorMessage);
  }, []);

  const handleDeleteJob = async (jobId) => {
    try {
      await deleteAnalysisJob(jobId);
      fetchSubmittedJobs();
    } catch (error) {
      setPageError(`Failed to delete job ${jobId}: ${error.message}`);
    }
  };

  useEffect(() => {
    const pollJobStatuses = async () => {
      if (submittedJobs.length === 0) return;

      const updatedJobsPromises = submittedJobs.map(async (job) => {
        if (job.status === "COMPLETED_SUCCESS" || job.status === "COMPLETED_FAIL") {
          return job; 
        }
        try {
          const updatedStatus = await getAnalysisJobStatus(job.job_id);
          return { ...job, ...updatedStatus }; 
        } catch (error) {
          console.error(`Failed to get status for job ${job.job_id}:`, error);
          return { ...job, error_polling: "Failed to update status" }; 
        }
      });
      
      const resolvedJobs = await Promise.all(updatedJobsPromises);
      setSubmittedJobs(resolvedJobs);
    };

    const intervalId = setInterval(pollJobStatuses, POLLING_INTERVAL);
    return () => clearInterval(intervalId);
  }, [submittedJobs]);

  return (
    <PageLayout
      title="Historical Data Analysis"
      backButton={{ label: 'Back to Research', to: '/research', preferHistory: false, hideLabelOnMobile: true }}
    >
      {pageError && <p className="mb-4 text-red-500 bg-red-100 p-3 rounded-md">{pageError}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Column 1: Instrument List and Job Submission Form */}
        <div className="md:col-span-1 space-y-6">
          <section className="bg-backgroundSurface p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-3 text-textPrimary">Add New Instrument</h2>
            <AddInstrumentForm 
              onInstrumentAdded={handleInstrumentAdded}
              onError={handleInstrumentAddError}
            />
            {instrumentMessage && <p className="mt-2 text-sm text-green-600">{instrumentMessage}</p>}
          </section>

          <section className="bg-backgroundSurface p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-3 text-textPrimary">Available Instruments</h2>
            {isLoadingInstruments && <p className="text-textSecondary">Loading instruments...</p>}
            {errorInstruments && <p className="text-red-500">Error: {errorInstruments}</p>}
            {!isLoadingInstruments && !errorInstruments && instruments.length === 0 && (
              <p className="text-textSecondary">No instruments available. You can add one above.</p>
            )}
            {!isLoadingInstruments && !errorInstruments && instruments.length > 0 && (
              <ul className="space-y-2 max-h-96 overflow-y-auto">
                {instruments.map((instrument) => (
                  <li key={instrument.symbol} className="p-3 bg-backgroundBody rounded shadow-sm">
                    <div className="font-medium text-textPrimary">{instrument.symbol} ({instrument.exchange})</div>
                    <div className="text-sm text-textSecondary">
                      Status: {InstrumentDataStatusDisplay[instrument.data_status] || instrument.data_status}
                    </div>
                    <div className="text-xs text-gray-400">
                      Base Timeframe: {instrument.base_timeframe}
                    </div>
                    {instrument.last_updated && (
                      <div className="text-xs text-gray-500">
                        Last Updated: {new Date(instrument.last_updated).toLocaleString()}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="bg-backgroundSurface p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-3 text-textPrimary">Submit New Analysis Job</h2>
            <AnalysisJobForm
              instruments={instruments}
              onJobSubmitted={handleJobSubmitted}
              onError={handleFormError}
            />
          </section>
        </div>

        {/* Column 2: Submitted Jobs List */}
        <div className="md:col-span-2 space-y-6">
          <section className="bg-backgroundSurface p-4 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-3 text-textPrimary">Submitted Analysis Jobs</h2>
            {isLoadingJobs && <p className="text-textSecondary">Loading submitted jobs...</p>}
            {!isLoadingJobs && submittedJobs.length === 0 && (
              <p className="text-textSecondary">No analysis jobs submitted yet.</p>
            )}
            {!isLoadingJobs && submittedJobs.length > 0 && (
              <ul className="space-y-3 max-h-[600px] overflow-y-auto"> {/* Added max-height and overflow */}
                {submittedJobs.map((job) => (
                  <li key={job.job_id} className="p-3 bg-backgroundBody rounded shadow-sm border border-borderDefault">
                    <div className="flex justify-between items-start"> {/* Changed items-center to items-start */}
                      <div className="flex-grow"> {/* Added flex-grow */}
                        <Link to={`/analysis/${job.job_id}`} className="text-lg font-semibold text-primary hover:underline break-all">
                          {job.name || `Job ID: ${job.job_id}`}
                        </Link>
                        <div className="text-sm text-textSecondary mt-1">
                          Type: {job.analysis_type.replace(/_/g, ' ')}
                        </div>
                        <div className="text-xs text-gray-400">
                          ID: {job.job_id}
                        </div>
                        <div className="text-sm text-textSecondary">
                          Instrument: {job.instrument_symbol} ({job.target_timeframe})
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2"> {/* Added flex-shrink-0 and ml-2 */}
                        <span 
                          className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${ /* Added whitespace-nowrap */
                            job.status === 'COMPLETED_SUCCESS' ? 'bg-green-100 text-green-700' :
                            job.status === 'COMPLETED_FAIL' ? 'bg-red-100 text-red-700' :
                            job.status === 'RUNNING_ANALYSIS' ? 'bg-blue-100 text-blue-700' :
                            job.status === 'UNKNOWN' ? 'bg-gray-100 text-gray-700' : // For unknown status
                            'bg-yellow-100 text-yellow-700' // Default for PENDING, FETCHING_DATA etc.
                          }`}
                        >
                          {AnalysisJobStatusDisplay[job.status] || job.status}
                        </span>
                        {job.error_polling && <p className="text-xs text-red-500 mt-1">{job.error_polling}</p>}
                        {job.status === 'UNKNOWN' && job.error_message && <p className="text-xs text-red-500 mt-1">{job.error_message}</p>}
                        {(job.status === 'COMPLETED_SUCCESS' || job.status === 'COMPLETED_FAIL') && (
                          <button
                            onClick={() => handleDeleteJob(job.job_id)}
                            className="mt-2 px-2 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      Submitted: {job.created_at ? new Date(job.created_at).toLocaleString() : 'N/A'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Last Update: {job.updated_at ? new Date(job.updated_at).toLocaleString() : 'N/A'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>
    </PageLayout>
  );
};

export default HistoricalAnalysisPage;
